import type { NotificationCandidate, NotificationPresentation } from './ports';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isOpaqueId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 128 && !hasControlCharacter(value);
}

function hasControlCharacter(value: string): boolean {
  return [...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
}

export function isCandidate(value: unknown): value is NotificationCandidate {
  return (
    isRecord(value) &&
    value['version'] === 1 &&
    ['id', 'scopeId', 'dedupeId', 'source', 'category'].every((key) => isOpaqueId(value[key])) &&
    typeof value['expiresAt'] === 'number' &&
    Number.isFinite(value['expiresAt']) &&
    'payload' in value
  );
}

/** Reject non-DTOs before serialization, then snapshot and cap the entire UTF-8 envelope. */
export function snapshotCandidate(value: unknown): NotificationCandidate | null {
  let visited = 0;
  const ancestors = new Set<object>();
  const valid = (item: unknown, depth: number): boolean => {
    visited += 1;
    if (visited > 4096 || depth > 8) return false;
    if (item === null || typeof item === 'boolean') return true;
    if (typeof item === 'number') return Number.isFinite(item);
    if (typeof item === 'string') return item.length <= 16384;
    if (typeof item !== 'object' || ancestors.has(item) || Object.getOwnPropertySymbols(item).length) return false;
    const prototype: unknown = Object.getPrototypeOf(item);
    if (prototype !== Object.prototype && prototype !== null && !(Array.isArray(item) && prototype === Array.prototype))
      return false;
    ancestors.add(item);
    const descriptors = Object.values(Object.getOwnPropertyDescriptors(item));
    const accepted =
      descriptors.length <= 4096 &&
      descriptors.every((descriptor) => 'value' in descriptor && valid(descriptor.value, depth + 1));
    ancestors.delete(item);
    return accepted;
  };
  try {
    if (!valid(value, 0)) return null;
    const json = JSON.stringify(value);
    if (new TextEncoder().encode(json).byteLength > 16384) return null;
    const snapshot: unknown = JSON.parse(json);
    return isCandidate(snapshot) ? snapshot : null;
  } catch {
    return null;
  }
}

export function isPresentation(value: unknown): value is NotificationPresentation {
  return (
    isRecord(value) &&
    typeof value['title'] === 'string' &&
    value['title'].length > 0 &&
    value['title'].length <= 120 &&
    typeof value['body'] === 'string' &&
    value['body'].length <= 300 &&
    typeof value['silent'] === 'boolean' &&
    !hasControlCharacter(value['title'] + value['body']) &&
    (value['viewKey'] === undefined || isOpaqueId(value['viewKey']))
  );
}
